<template>
  <VaModal
    v-model="showWarning"
    :title="'Session Timeout Warning'"
    :message="warningMessage"
    :ok-text="'Stay Logged In'"
    :cancel-text="'Logout Now'"
    :hide-default-actions="false"
    :close-on-escape="false"
    :close-on-overlay-click="false"
    @ok="extendSession"
    @cancel="logoutNow"
  >
    <template #default>
      <div class="session-warning">
        <div class="warning-icon">
          <VaIcon name="warning" color="warning" size="large" />
        </div>
        <div class="warning-content">
          <h3>Your session will expire soon</h3>
          <p>You have been inactive for a while. Your session will expire in {{ timeRemaining }} seconds.</p>
          <p>Click "Stay Logged In" to continue your session, or "Logout Now" to sign out immediately.</p>
        </div>
      </div>
    </template>
  </VaModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { authService } from '../services/authService'
import { showSuccess } from '../services/notificationService'

const router = useRouter()

// Configuration
const WARNING_TIME = 2 * 60 * 1000 // Show warning 2 minutes before expiry
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes total

const showWarning = ref(false)
const timeRemaining = ref(0)
const countdownInterval = ref<NodeJS.Timeout | null>(null)
const lastActivity = ref(Date.now())

// Computed warning message
const warningMessage = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60)
  const seconds = timeRemaining.value % 60
  return `Your session will expire in ${minutes}:${seconds.toString().padStart(2, '0')} due to inactivity.`
})

// Check session status
const checkSessionStatus = () => {
  const now = Date.now()
  const timeSinceLastActivity = now - lastActivity.value
  const timeUntilExpiry = SESSION_TIMEOUT - timeSinceLastActivity

  if (timeUntilExpiry <= 0) {
    // Session expired
    authService.logout('Session expired due to inactivity', router)
    return
  }

  if (timeUntilExpiry <= WARNING_TIME && !showWarning.value) {
    // Show warning
    showWarning.value = true
    startCountdown(timeUntilExpiry)
  }
}

// Start countdown timer
const startCountdown = (initialTime: number) => {
  timeRemaining.value = Math.floor(initialTime / 1000)

  countdownInterval.value = setInterval(() => {
    timeRemaining.value--

    if (timeRemaining.value <= 0) {
      clearInterval(countdownInterval.value!)
      authService.logout('Session expired due to inactivity', router)
    }
  }, 1000)
}

// Extend session
const extendSession = async () => {
  try {
    // Reset activity timestamp
    lastActivity.value = Date.now()

    // Clear countdown
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
      countdownInterval.value = null
    }

    // Hide warning
    showWarning.value = false

    // Try to refresh token
    await authService.refreshToken()

    showSuccess('Session extended successfully')
  } catch (error) {
    console.error('Failed to extend session:', error)
    authService.logout('Failed to extend session', router)
  }
}

// Logout now
const logoutNow = () => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }

  showWarning.value = false
  authService.logout('Logged out by user', router)
}

// Setup activity listeners
const setupActivityListeners = () => {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

  const updateActivity = () => {
    lastActivity.value = Date.now()
  }

  events.forEach((event) => {
    document.addEventListener(event, updateActivity, true)
  })

  // Return cleanup function
  return () => {
    events.forEach((event) => {
      document.removeEventListener(event, updateActivity, true)
    })
  }
}

// Setup session monitoring
const setupSessionMonitoring = () => {
  const checkInterval = setInterval(checkSessionStatus, 10000) // Check every 10 seconds

  return () => {
    clearInterval(checkInterval)
  }
}

onMounted(() => {
  const cleanupActivity = setupActivityListeners()
  const cleanupMonitoring = setupSessionMonitoring()

  onUnmounted(() => {
    cleanupActivity()
    cleanupMonitoring()
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
    }
  })
})
</script>

<style scoped>
.session-warning {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
}

.warning-icon {
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-content h3 {
  margin: 0 0 0.5rem 0;
  color: var(--va-text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.warning-content p {
  margin: 0.25rem 0;
  color: var(--va-text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}
</style>
